import { z } from "zod";
import { zExt } from "../common/zod-util";
import { Provider } from "../order";
import { RateSchema } from "../outbound-labels/outbound-labels";
import { ParcelSchema } from "../outbound-labels/parcel";
import { RedoShopifyFulfillmentDisplayStatus } from "./fulfillment-display-status";
import { FulfillmentLineItemSchema } from "./fulfillment-line-item";
import { FulfillmentOrderAddressSchema } from "./fulfillment-order-address";
import { RedoShopifyFulfillmentStatus } from "./fulfillment-status";

export const RedoFulfillmentSchema = z.object({
  provider: z.nativeEnum(Provider),
  externalId: z.string(),
  name: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  estimatedDeliveryAt: z.date().nullish(),
  originAddress: FulfillmentOrderAddressSchema,
  destinationAddress: FulfillmentOrderAddressSchema,
  inTransitAt: z.date().nullish(),
  deliveredAt: z.date().nullish(),
  displayStatus: z.nativeEnum(RedoShopifyFulfillmentDisplayStatus).nullish(),
  status: z.nativeEnum(RedoShopifyFulfillmentStatus),
  requiresShipping: z.boolean(),
  totalQuantity: z.number(),
  lineItems: z.array(FulfillmentLineItemSchema),
  trackingInfo: z.array(
    z.object({
      company: z.string().nullish(),
      url: z.string().nullish(),
      number: z.string().nullish(),
    }),
  ),
  order: z
    .object({
      id: z.string(),
      name: z.string(),
      tags: z.array(z.string()).nullish(),
      note: z.string(),
      createdAt: z.date(),
    })
    .nullish(),
  customer: z
    .object({
      id: z.string(),
      name: z.string(),
      tags: z.array(z.string()).nullish(),
      numOrders: z.number(),
      email: z.string().nullish(),
      phone: z.string().nullish(),
      note: z.string().nullish(),
    })
    .nullish(),
  raw: z.any(),
});
export type RedoFulfillment = z.infer<typeof RedoFulfillmentSchema>;

export const OutboundShipmentLabelSchema = z.object({
  url: z.string(),
  printed: z.date().optional(),
  commercialInvoiceUrl: z.string().optional().nullish(),
  labelDate: z.date().nullish(),
});
export type OutboundShipmentLabel = z.infer<typeof OutboundShipmentLabelSchema>;

export const OutboundShipmentLineItemSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  sku: z.string().nullish(),
});
export type OutboundShipmentLineItem = z.infer<
  typeof OutboundShipmentLineItemSchema
>;

export const LineItemsByFulfillmentOrderSchema = z.array(
  z.object({
    fulfillmentOrderId: z.string(),
    fulfillmentOrderLineItems: z.array(OutboundShipmentLineItemSchema),
  }),
);
export type LineItemsByFulfillmentOrder = z.infer<
  typeof LineItemsByFulfillmentOrderSchema
>;

export const ShipmentTrackerSchema = z.object({
  id: z.string(),
  status: z.string(),
  trackingCode: z.string(),
  carrier: z.string(),
  publicUrl: z.string(),
  raw: z.any(),
});

export const ShipmentPackageSchema = z.object({
  label: OutboundShipmentLabelSchema.nullish(),
  boughtRate: RateSchema.nullish(),
  parcel: ParcelSchema.nullish(),
  lineItemsByFulfillmentOrder: LineItemsByFulfillmentOrderSchema,
  slip: z.object({ printed: z.date().nullish() }).nullish(),
  labelSlipUrl: z.string().nullish(),
  scanFormId: z.string().nullish(),
  voided: z.boolean().nullish(),
  tracker: ShipmentTrackerSchema.nullish(),
  insured: z.boolean().nullish(),
  insuredValue: z.number().nullish(),
});
export type ShipmentPackage = z.infer<typeof ShipmentPackageSchema>;

export const OutboundShipmentSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  createdAt: z.date(),
  updatedAt: z.date(),
  fulfillments: z.array(RedoFulfillmentSchema),
  shipmentPackages: z.array(ShipmentPackageSchema),
});

export type OutboundShipment = z.infer<typeof OutboundShipmentSchema>;

export function getDefaultFulfillment(
  shipment: OutboundShipment,
): RedoFulfillment {
  const fulfillment = shipment.fulfillments?.[0];
  if (!fulfillment) {
    throw new Error("No fulfillments on shipment");
  }
  return fulfillment;
}
